import { SimulationLinkDatum, SimulationNodeDatum } from 'd3-force';

import { ContextGraphVisualizationNode } from '@/types/context-graph-types';

export interface SimNode
  extends SimulationNodeDatum,
    ContextGraphVisualizationNode {
  radius: number;
  startTime: number | null;
}

export interface SimLink extends SimulationLinkDatum<SimNode> {
  id: string;
  label: string;
  fact: string;
  validAt?: string | null;
  curve: number;
  startTime: number | null;
  endTime: number;
  // Parallel facts beyond the arc cap are kept in the data (searchable,
  // counted) but not rendered — email-style graphs can have hundreds of
  // facts between one pair, which would fan out into giant arcs.
  hidden?: boolean;
  pairCount?: number;
}

export type GraphSelection =
  | { kind: 'node'; node: SimNode }
  | { kind: 'edge'; link: SimLink };

export const nodeRadius = (degree: number): number =>
  Math.min(4.5 + Math.sqrt(degree) * 2.4, 20);

export const truncateLabel = (label: string, max = 26): string =>
  label.length > max ? `${label.slice(0, max - 1)}…` : label;

export const parseTime = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatValidAt = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

export const linkEndpoints = (
  link: SimLink,
): { source: SimNode; target: SimNode } => ({
  source: link.source as SimNode,
  target: link.target as SimNode,
});

export const linkEndpointIds = (link: SimLink): [string, string] => [
  typeof link.source === 'object' ? link.source.id : String(link.source),
  typeof link.target === 'object' ? link.target.id : String(link.target),
];

export const linkPath = (link: SimLink): string => {
  const { source, target } = linkEndpoints(link);
  const x1 = source.x ?? 0;
  const y1 = source.y ?? 0;
  const x2 = target.x ?? 0;
  const y2 = target.y ?? 0;
  if (link.curve === 0) {
    return `M ${x1} ${y1} L ${x2} ${y2}`;
  }
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  const mx = (x1 + x2) / 2 + link.curve * 16 * (-dy / length);
  const my = (y1 + y2) / 2 + link.curve * 16 * (dx / length);
  return `M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`;
};

export const linkMidpoint = (link: SimLink): { x: number; y: number } => {
  const { source, target } = linkEndpoints(link);
  const x1 = source.x ?? 0;
  const y1 = source.y ?? 0;
  const x2 = target.x ?? 0;
  const y2 = target.y ?? 0;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const length = Math.hypot(dx, dy) || 1;
  return {
    x: (x1 + x2) / 2 + link.curve * 8 * (-dy / length),
    y: (y1 + y2) / 2 + link.curve * 8 * (dx / length),
  };
};
